import { Icon } from '@iconify/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { type FC, useEffect, useState } from 'react';

import LogoWhite from '@/assets/logo-mindpal.svg';
import LogoPrimary from '@/assets/logo-primary.svg';
import NavigationButton from '@/components/NavigationButton';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useHiddenHeader } from '@/hooks/useHiddenHeader';

import type { DropdownColumn } from '../Desktop/NavDropdown';
import { DropdownLinkItem, LinkWrapper } from '../Desktop/NavDropdown';
import { useNavigation } from '../useNavigation';

const SubMenuView: FC<{
  columns: DropdownColumn[];
  onBack: () => void;
  theme: 'dark' | 'light';
}> = ({ columns, onBack, theme }) => (
  <div className="flex flex-1 flex-col gap-6 overflow-auto">
    <button
      type="button"
      onClick={onBack}
      className={classNames('flex w-fit items-center gap-2 text-base', {
        'text-white': theme === 'dark',
        'text-jb-neutral-1000': theme === 'light',
      })}
    >
      <Icon icon="material-symbols:arrow-back-rounded" fontSize={20} />
      Back
    </button>

    {columns.map(({ title, description, links, link: titleLink }) => (
      <div key={title} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          {titleLink ? (
            <LinkWrapper
              link={{ label: title, ...titleLink }}
              className="w-fit"
            >
              <span className="text-base font-semibold text-jb-primary-400">
                {title}
              </span>
            </LinkWrapper>
          ) : (
            <span className="text-base font-semibold text-jb-primary-400">
              {title}
            </span>
          )}
          {description && (
            <span
              className={classNames('text-xs leading-[1.3]', {
                'text-white/60': theme === 'dark',
                'text-jb-neutral-700': theme === 'light',
              })}
            >
              {description}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {links.map((link) => (
            <DropdownLinkItem
              key={link.label}
              link={link}
              variant={theme === 'light' ? 'dark' : 'light'}
              className="whitespace-normal break-words"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

type Props = {
  theme?: 'dark' | 'light';
};

const MobileHeader: FC<Props> = ({ theme = 'dark' }) => {
  const { show, isOnTop } = useHiddenHeader();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const { pathname } = useRouter();
  const navigation = useNavigation();

  useEffect(() => {
    setSheetOpen(false);
    setActiveSubmenu(null);
  }, [pathname]);

  const activeItem = navigation.find((item) => item.label === activeSubmenu);

  return (
    <div
      className={classNames(
        'fixed left-0 top-0 z-40 flex w-full items-center justify-between  py-5 transition-all duration-300 lg:hidden',
        {
          'bg-gradient-to-tr from-black/90 to-secondary-900/90':
            !isOnTop && theme === 'dark',
          'bg-jb-neutral-100 shadow-md': !isOnTop && theme === 'light',
          '-translate-y-full pointer-events-none': !show && !isOnTop,
          'px-8': isOnTop,
          'px-4': !isOnTop,
        }
      )}
    >
      {/* Full page reload is intentional: routes through nginx to the Lovable homepage */}
      {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
      <a href="/" className="inline-block transition-transform hover:scale-105">
        <div className="w-28">
          {theme === 'light' ? <LogoPrimary /> : <LogoWhite />}
        </div>
      </a>

      <Sheet
        open={sheetOpen}
        onOpenChange={(next) => {
          setSheetOpen(next);
          if (!next) setActiveSubmenu(null);
        }}
      >
        <SheetTrigger>
          <Icon
            icon="mdi:hamburger-menu"
            className={
              theme === 'light' ? 'text-jb-neutral-1000' : 'text-white'
            }
            fontSize={28}
          />
        </SheetTrigger>

        <SheetContent
          className={classNames(
            'flex w-11/12 !max-w-none flex-col gap-8 overflow-auto border-0 px-8 py-8',
            {
              'bg-black [&>button>svg]:text-white': theme === 'dark',
              'bg-jb-neutral-100 [&>button>svg]:text-jb-neutral-1000':
                theme === 'light',
            }
          )}
        >
          {/* Full page reload is intentional: routes through nginx to the Lovable homepage */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/"
            className="inline-block w-fit transition-transform hover:scale-105"
          >
            <div className="w-28">
              {theme === 'light' ? <LogoPrimary /> : <LogoWhite />}
            </div>
          </a>

          <div className="flex min-h-0 flex-1 flex-col overflow-auto">
            {activeSubmenu && activeItem ? (
              <SubMenuView
                columns={activeItem.columns}
                onBack={() => setActiveSubmenu(null)}
                theme={theme}
              />
            ) : (
              <div className="flex flex-col gap-1">
                {navigation.map(({ label, columns }) => (
                  <button
                    key={label}
                    type="button"
                    aria-haspopup={columns.length > 0 ? 'true' : undefined}
                    aria-expanded={
                      columns.length > 0 ? activeSubmenu === label : undefined
                    }
                    onClick={() =>
                      columns.length > 0 && setActiveSubmenu(label)
                    }
                    className={classNames(
                      'flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-2 py-3 text-left text-lg transition-colors',
                      {
                        'text-white hover:bg-white/10': theme === 'dark',
                        'text-jb-neutral-1000 hover:bg-black/5':
                          theme === 'light',
                      }
                    )}
                  >
                    <span className="truncate">{label}</span>
                    {columns.length > 0 && (
                      <Icon
                        icon="iconamoon:arrow-right-2-light"
                        aria-hidden="true"
                        className="shrink-0"
                        fontSize={20}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <NavigationButton
            href="/?contact=true"
            isInternalLink
            variant="unstyled"
            className="flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-jb-primary-500 px-4 text-h5 font-semibold text-white transition-colors hover:bg-jb-primary-400"
            suffixIcon="material-symbols:arrow-forward"
          >
            Let&apos;s talk
          </NavigationButton>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileHeader;
